import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Popover, PopoverProps } from '../KitPopover';
import colors from '../../common/theme/colors';
import constants from '../../common/theme/constants';
import KitButton from '../KitButton';
import KitDropDownItem, { DropDownItemEventObject, DropDownItemProps } from './KitDropDownItem';
import KitDropDownMenu from './KitDropDownMenu';
import KitIcon from '../KitIcon';
import { focusFirstDescendant } from '../../common/aria';
import { EventName, PopOverPosition } from '../../common/types';
import { onEscape, onFocusOut as onFocusOutAction } from '../../common/events';

export interface KitDropdownActionListProps {
  id?: string;
  items: DropDownItemProps[];
  toggleAriaLabel?: string;
  toggleLabel?: string;
  label?: string;
  onSelect?: (eventObject: DropDownItemEventObject) => void;
  popOverProps?: Partial<PopoverProps>; // To override popOverProps
  hidePopOverOnItemClick?: boolean;
  dropdownItemBg?: string;
  positions?: PopOverPosition[];
  padding?: number;
  qaId?: string;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-weight: ${constants.fontWeight.normal};
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ActionButton = styled(KitButton)`
  display: flex;
  overflow: hidden;
  flex-direction: row;
  align-items: center;
  background-color: transparent;
  padding: 0 ${constants.spacing.absolute.xs}px;
  margin: 0 ${constants.spacing.absolute.xs}px;
  &.link:hover {
    background-color: transparent;
  }
`;

const DropDownLabel = styled.div`
  color: ${colors.gray_50};
  padding: 0 ${constants.spacing.absolute.xs}px;
  margin: 0 ${constants.spacing.absolute.xs}px;
`;

const CustomCircleToggleLabel = styled.span`
  padding-right: ${constants.spacing.s}rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
  overflow: hidden;
  max-width: 100%;
`;

function KitDropdownActionList({
  dropdownItemBg,
  items,
  label,
  toggleLabel,
  toggleAriaLabel,
  popOverProps,
  hidePopOverOnItemClick = true,
  positions,
  padding = 10,
  onSelect,
  qaId,
}: KitDropdownActionListProps): JSX.Element {
  const defaultPositions: PopOverPosition[] = ['bottom', 'right', 'left', 'top'];
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DropDownItemEventObject>({
    value: '',
    label: toggleLabel,
  });

  const dropdownMenu = useRef(null);
  const actionButtonRef = useRef(null);
  const firstItemRef = useRef(null);
  const lastFocus = useRef(null);

  const onKeyPressListener = (event) => onEscape(event, setIsPopoverOpen, actionButtonRef.current);
  const onFocusOut = (event) => onFocusOutAction(event, dropdownMenu, firstItemRef, lastFocus);

  useEffect(() => {
    const removeAllListeners = () => {
      document.removeEventListener(EventName.FOCUSOUT, onFocusOut, true);
      document.removeEventListener(EventName.KEYUP, onKeyPressListener);
      lastFocus.current = null; // forget last focus when menu is closed
    };

    if (isPopoverOpen) {
      focusFirstDescendant(dropdownMenu.current);
      document.addEventListener(EventName.FOCUSOUT, onFocusOut, true);
      document.addEventListener(EventName.KEYUP, onKeyPressListener);
    } else {
      removeAllListeners();
    }
    return () => {
      removeAllListeners();
    };
  }, [isPopoverOpen]);

  const handleItemClick = (eventObject: DropDownItemEventObject) => {
    setSelectedItem(eventObject);
    if (eventObject.onSelect) {
      eventObject.onSelect();
    }
    if (onSelect) {
      onSelect(eventObject);
    }
    if (hidePopOverOnItemClick) {
      setIsPopoverOpen(false);
    }
  };
  const ariaLabel = toggleAriaLabel || toggleLabel;
  const menu = (
    <KitDropDownMenu ref={dropdownMenu} bg={dropdownItemBg}>
      {items.map((item, i) => {
        return (
          <KitDropDownItem
            key={item.value}
            ref={i === 0 ? firstItemRef : null}
            active={item.value === selectedItem.value}
            onClick={handleItemClick}
            {...item}
          />
        );
      })}
    </KitDropDownMenu>
  );
  return (
    <Wrapper>
      {label && <DropDownLabel>{label}</DropDownLabel>}
      <DropdownWrapper>
        <Popover
          reposition
          isOpen={isPopoverOpen}
          positions={positions ?? defaultPositions}
          padding={padding}
          onClickOutside={() => setIsPopoverOpen(false)}
          content={menu}
          align='start'
          ref={actionButtonRef}
          containerStyle={{ zIndex: '1' }}
          {...popOverProps}
        >
          <ActionButton
            variant='link'
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            aria-label={ariaLabel}
            aria-expanded={isPopoverOpen}
            aria-haspopup='true'
            data-qa-id={qaId}
          >
            {toggleLabel && (
              <CustomCircleToggleLabel>{selectedItem.label || toggleLabel}</CustomCircleToggleLabel>
            )}
            <KitIcon icon='angle-down' fill={colors.gray_70} />
          </ActionButton>
        </Popover>
      </DropdownWrapper>
    </Wrapper>
  );
}

export default KitDropdownActionList;
